import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import { RolesByType } from '../DLOFilterByRoleModal';

// Reuse the same interface as Schedule table (with fcst/schd fields)
// We'll interpret fcst as EST and schd as ACTUAL in the actual hours context
export interface ActualHoursRowData {
  id: string;
  roleName: string;
  sunday: { fcst: number; schd: number };
  monday: { fcst: number; schd: number };
  tuesday: { fcst: number; schd: number };
  wednesday: { fcst: number; schd: number };
  thursday: { fcst: number; schd: number };
  friday: { fcst: number; schd: number };
  saturday: { fcst: number; schd: number };
  total: { fcst: number; schd: number };
  variance: number;
}

export interface DLOCreateActualHoursTableContentProps {
  data?: ActualHoursRowData[];
  style?: ViewStyle;
  filteredRoles?: RolesByType;
}

// Default data
const defaultData: ActualHoursRowData[] = [
  {
    id: '1',
    roleName: 'PICKER',
    sunday: { fcst: 0, schd: 0 },
    monday: { fcst: 0, schd: 0 },
    tuesday: { fcst: 0, schd: 0 },
    wednesday: { fcst: 0, schd: 0 },
    thursday: { fcst: 0, schd: 0 },
    friday: { fcst: 0, schd: 0 },
    saturday: { fcst: 0, schd: 0 },
    total: { fcst: 0, schd: 0 },
    variance: 0,
  },
  {
    id: '2',
    roleName: 'Totals (All Roles)',
    sunday: { fcst: 0, schd: 0 },
    monday: { fcst: 0, schd: 0 },
    tuesday: { fcst: 0, schd: 0 },
    wednesday: { fcst: 0, schd: 0 },
    thursday: { fcst: 0, schd: 0 },
    friday: { fcst: 0, schd: 0 },
    saturday: { fcst: 0, schd: 0 },
    total: { fcst: 0, schd: 0 },
    variance: 0,
  },
];

const DLOCreateActualHoursTableContent: React.FC<DLOCreateActualHoursTableContentProps> = ({
  data = defaultData,
  style,
  filteredRoles,
}) => {
  // Helper function to format numbers to 2 decimal places
  const formatToTwoDecimals = (value: number): string => {
    return value.toFixed(2);
  };

  // Function to get color for actual hours based on estimated comparison
  const getActualHoursColor = (estHours: number, actualHours: number, roleName?: string): string => {
    let effectiveEst = estHours;
    
    console.log(`Calculating color for role: ${roleName}, est: ${effectiveEst}, actual: ${actualHours}`);
    // If estimate is 0, don't apply color logic
    if (effectiveEst === 0 && actualHours === 0) {
      return '#080707ff'; // Default black color
    }

    // Calculate 10% bounds
    const lowerBound = effectiveEst - (effectiveEst * 0.10);
    const upperBound = effectiveEst + (effectiveEst * 0.10);

    // Determine color based on range
    if (actualHours >= lowerBound && actualHours <= upperBound) {
      return '#008553'; // Green - within 10% range
    } else if (actualHours < lowerBound) {
      return '#3A3A3A'; // Black - below range
    } else {
      return '#DC2626'; // Red - above range
    }
  };

  // Function to simplify role name by removing role type suffixes/prefixes
  const getSimplifiedRoleName = (roleName: string): string => {
    // If it's "Totals (All Roles)", keep it as "Totals"
    if (roleName.includes('Totals')) {
      return 'Totals (All Roles)';
    }
    
    // For other roles, remove common role type patterns
    let simplified = roleName
      .replace(/\s*\(.*\)$/, '') // Remove anything in parentheses at the end
      .replace(/\s*-\s*\w+$/, '') // Remove dash followed by role type at the end
      .replace(/^\w+\s*-\s*/, '') // Remove role type prefix with dash
      .trim();
    
    return simplified || roleName; // Return original if simplified is empty
  };

  // Function to check if a role is enabled based on filtered roles
  const isRoleEnabled = (roleName: string): boolean => {
    if (!filteredRoles) return true; // If no filter is set, show all roles
    
    // Always show "Totals" rows
    if (roleName.includes('Totals')) {
      return true;
    }
    
    // Check all role categories to see if this role is enabled
    const allRoles = [
      ...filteredRoles.DSD,
      ...filteredRoles['NON-DSD'],
      ...filteredRoles.Retail,
      ...filteredRoles.Other,
    ];
    
    // Find matching role by name (case insensitive, simplified comparison)
    const matchingRole = allRoles.find(role => {
      const simplifiedRoleName = getSimplifiedRoleName(roleName).toLowerCase();
      const simplifiedFilterName = role.name.toLowerCase();
      return simplifiedRoleName.includes(simplifiedFilterName) || 
             simplifiedFilterName.includes(simplifiedRoleName);
    });
    
    return matchingRole ? matchingRole.enabled : true; // Default to enabled if not found
  };

  // Process data - interpret fcst as EST and schd as ACTUAL
  const processedData = data.map(item => {
    const simplifiedRole = getSimplifiedRoleName(item.roleName).trim().toUpperCase();
    const isLayerRole = simplifiedRole === 'LAYER SUPPORT' || simplifiedRole === 'LAYER';

    // Only double the EST (fcst) values for LAYER/LAYER SUPPORT
    const doubleEst = (est: number) => isLayerRole ? est * 2 : est;

    // fcst represents EST, schd represents ACTUAL
    return {
      ...item,
      sunday: { est: doubleEst(item.sunday.fcst), actual: item.sunday.schd },
      monday: { est: doubleEst(item.monday.fcst), actual: item.monday.schd },
      tuesday: { est: doubleEst(item.tuesday.fcst), actual: item.tuesday.schd },
      wednesday: { est: doubleEst(item.wednesday.fcst), actual: item.wednesday.schd },
      thursday: { est: doubleEst(item.thursday.fcst), actual: item.thursday.schd },
      friday: { est: doubleEst(item.friday.fcst), actual: item.friday.schd },
      saturday: { est: doubleEst(item.saturday.fcst), actual: item.saturday.schd },
      total: { est: doubleEst(item.total.fcst), actual: item.total.schd },
      variance: doubleEst(item.total.fcst) - item.total.schd
    };
  });

  // Filter data based on enabled roles
  const filteredData = processedData.filter(item => {
    const isEnabled = isRoleEnabled(item.roleName);
    
    // Always show totals rows regardless of variance or filter state  
    const isTotalsRow = item.roleName.toLowerCase().includes('totals');
    if (isTotalsRow) {
      return true;
    }
    
    return isEnabled;
  });

  // Function to calculate totals from filtered data (excluding existing totals rows)
  const calculateTotalsFromFilteredData = (filteredData: any[]): any => {
    // Filter out any existing totals rows to avoid double counting
    const dataWithoutTotals = filteredData.filter(item => !item.roleName.toLowerCase().includes('totals'));
    
    // Initialize totals (using est/actual from processed data)
    const totals = {
      sunday: { est: 0, actual: 0 },
      monday: { est: 0, actual: 0 },
      tuesday: { est: 0, actual: 0 },
      wednesday: { est: 0, actual: 0 },
      thursday: { est: 0, actual: 0 },
      friday: { est: 0, actual: 0 },
      saturday: { est: 0, actual: 0 },
      total: { est: 0, actual: 0 },
    };

    // Sum up all values from all roles (processed data has est/actual)
    dataWithoutTotals.forEach((item: any) => {
      totals.sunday.est += item.sunday.est;
      totals.sunday.actual += item.sunday.actual;
      totals.monday.est += item.monday.est;
      totals.monday.actual += item.monday.actual;
      totals.tuesday.est += item.tuesday.est;
      totals.tuesday.actual += item.tuesday.actual;
      totals.wednesday.est += item.wednesday.est;
      totals.wednesday.actual += item.wednesday.actual;
      totals.thursday.est += item.thursday.est;
      totals.thursday.actual += item.thursday.actual;
      totals.friday.est += item.friday.est;
      totals.friday.actual += item.friday.actual;
      totals.saturday.est += item.saturday.est;
      totals.saturday.actual += item.saturday.actual;
      totals.total.est += item.total.est;
      totals.total.actual += item.total.actual;
    });

    // Calculate variance as difference between total est and total actual
    const variance = totals.total.est - totals.total.actual;
    console.log("Calculated Actual Hours Totals Variance:", variance, totals);
    
    return {
      id: 'calculated-totals',
      roleName: 'Totals (All Roles)',
      sunday: { 
        est: parseFloat(totals.sunday.est.toFixed(2)), 
        actual: parseFloat(totals.sunday.actual.toFixed(2)) 
      },
      monday: { 
        est: parseFloat(totals.monday.est.toFixed(2)), 
        actual: parseFloat(totals.monday.actual.toFixed(2)) 
      },
      tuesday: { 
        est: parseFloat(totals.tuesday.est.toFixed(2)), 
        actual: parseFloat(totals.tuesday.actual.toFixed(2)) 
      },
      wednesday: { 
        est: parseFloat(totals.wednesday.est.toFixed(2)), 
        actual: parseFloat(totals.wednesday.actual.toFixed(2)) 
      },
      thursday: { 
        est: parseFloat(totals.thursday.est.toFixed(2)), 
        actual: parseFloat(totals.thursday.actual.toFixed(2)) 
      },
      friday: { 
        est: parseFloat(totals.friday.est.toFixed(2)), 
        actual: parseFloat(totals.friday.actual.toFixed(2)) 
      },
      saturday: { 
        est: parseFloat(totals.saturday.est.toFixed(2)), 
        actual: parseFloat(totals.saturday.actual.toFixed(2)) 
      },
      total: { 
        est: parseFloat(totals.total.est.toFixed(2)), 
        actual: parseFloat(totals.total.actual.toFixed(2)) 
      },
      variance: parseFloat(variance.toFixed(2)),
    };
  };

  // Remove any existing totals rows from filtered data and add calculated totals
  const dataWithoutExistingTotals = filteredData.filter(item => !item.roleName.toLowerCase().includes('totals'));
  const calculatedTotals = calculateTotalsFromFilteredData(filteredData);
  const finalData = [...dataWithoutExistingTotals, calculatedTotals];

  const renderDataRow = (item: any) => {
    const isTotalsRow = item.roleName.toLowerCase().includes('totals');
    const simplifiedRole = getSimplifiedRoleName(item.roleName).trim().toUpperCase();
    const isLayerSupport = simplifiedRole === 'LAYER SUPPORT' || simplifiedRole === 'LAYER';

    return (
      <View key={item.id} style={[styles.dataRow, isTotalsRow && styles.totalsRow]}>
        {/* Left section - Role name */}
        <View style={styles.leftSection}>
          <Text style={[styles.roleNameText, isTotalsRow && styles.totalsRoleNameText]}>
            {getSimplifiedRoleName(item.roleName)}
          </Text>
        </View>

        {/* Major divider between ROLES and days */}
        <View style={styles.majorDivider} />

        {/* Data section */}
        <View style={styles.dataSection}>
          {/* Sunday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.sunday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.sunday.est, item.sunday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.sunday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Monday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.monday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.monday.est, item.monday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.monday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Tuesday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.tuesday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.tuesday.est, item.tuesday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.tuesday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Wednesday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.wednesday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.wednesday.est, item.wednesday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.wednesday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Thursday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.thursday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.thursday.est, item.thursday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.thursday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Friday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.friday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.friday.est, item.friday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.friday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Saturday */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.saturday.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.saturday.est, item.saturday.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.saturday.actual)}
              </Text>
            </View>
          </View>
          <View style={styles.majorDivider} />

          {/* Total */}
          <View style={styles.dataCellContainer}>
            <View style={styles.dataCell}>
              <Text style={[styles.dataText, isTotalsRow && styles.totalsDataText]}>{formatToTwoDecimals(item.total.est)}</Text>
            </View>
            <View style={styles.cellDivider} />
            <View style={styles.dataCell}>
              <Text style={[
                styles.dataText, 
                isTotalsRow && styles.totalsDataText,
                { color: getActualHoursColor(item.total.est, item.total.actual, item.roleName) }
              ]}>
                {formatToTwoDecimals(item.total.actual)}
              </Text>
            </View>
          </View>
        </View>

        {/* Major divider between days and variance */}
        <View style={styles.majorDivider} />

        {/* Variance section */}
        <View style={styles.varianceSection}>
          <Text style={[
            styles.varianceText, 
            isTotalsRow && styles.totalsVarianceText,
            item.variance > 0 && styles.positiveVariance,
            item.variance < 0 && styles.negativeVariance
          ]}>
            {formatToTwoDecimals(Math.abs(item.variance))}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {finalData.map(renderDataRow)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    backgroundColor: '#FFFFFF',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftColor: '#DFDFDF',
    borderRightColor: '#DFDFDF',
    borderBottomColor: '#DFDFDF',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  dataRow: {
    width: 1416,
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
    justifyContent: 'center',
  },
  leftSection: {
    width: 227,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  roleNameText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
    marginLeft: -1,
  },
  dataSection: {
    width: 1067,
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
  },
  dataCellContainer: {
    width: 132,
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  dataCell: {
    width: 54,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dataText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  cellDivider: {
    width: 1,
    height: 56,
    backgroundColor: '#DFDFDF',
  },
  varianceSection: {
    width: 121,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
  varianceText: {
    color: '#008553',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  majorDivider: {
    width: 1,
    height: 56,
    backgroundColor: '#DFDFDF',
  },
  totalsRow: {
    backgroundColor: '#F8F9FA',
  },
  totalsRoleNameText: {
    color: '#000000',
  },
  totalsDataText: {
    color: '#000000',
  },
  totalsVarianceText: {
    color: '#008553',
  },
  positiveVariance: {
    color: '#008553',
  },
  negativeVariance: {
    color: '#D32F2F',
  },
});

export default DLOCreateActualHoursTableContent;
